import { useOptimistic, useTransition } from "react";
import { Switch } from "../../switch";
import { Button } from "../../button";
import { SectionTag } from "../section-tag";
import { SettingsSection } from "./settings-section";
import { cn } from "../../utils";

export interface SectionEnablementVm {
  slug: string;
  name: string;
  enabled: boolean;
  legacy: boolean;
  description: string;
  /** Short status hint shown under the section name (e.g. "active · 5/5 filled"). */
  statusHint?: string | null;
}

export interface SectionEnablementListProps {
  rows: SectionEnablementVm[];
  /** Optimistic + transitional toggle. Throws on failure → row reverts. */
  onSetEnabled: (slug: string, enabled: boolean) => Promise<void>;
  scope: "workspace" | "project";
}

/**
 * Two-card list of canonical sections — "Active" and "More sections
 * you can enable." Both lists use the standard list-row pattern. The
 * toggle and the Enable button both go through the same server action;
 * UI feedback is optimistic (flip immediately, revert on error).
 */
export function SectionEnablementList({
  rows,
  onSetEnabled,
  scope,
}: SectionEnablementListProps) {
  const [optimisticRows, setOptimistic] = useOptimistic<
    SectionEnablementVm[],
    { slug: string; enabled: boolean }
  >(rows, (state, { slug, enabled }) =>
    state.map((r) => (r.slug === slug ? { ...r, enabled } : r)),
  );
  const [pending, startTransition] = useTransition();

  function setEnabled(slug: string, enabled: boolean) {
    startTransition(async () => {
      setOptimistic({ slug, enabled });
      try {
        await onSetEnabled(slug, enabled);
      } catch (err) {
        // Optimistic rolls back on its own when the action throws;
        // surface the failure in the console — toast wiring lives at
        // the page level if needed.
        console.error("Failed to update section enablement", err);
      }
    });
  }

  const active = optimisticRows.filter((r) => r.enabled);
  const inactive = optimisticRows.filter((r) => !r.enabled);
  const scopeLabel =
    scope === "workspace" ? "this workspace" : "this project";

  return (
    <>
      <SettingsSection title={`Active in ${scopeLabel}`}>
        {active.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No sections enabled. Enable one below.
          </p>
        ) : (
          <ol>
            {active.map((row, idx) => (
              <li
                key={row.slug}
                className={cn(
                  "flex items-center justify-between gap-3 border-b border-border/40 py-2",
                  idx === 0 && "pt-0",
                  idx === active.length - 1 && "border-b-0 pb-0",
                )}
              >
                <div className="flex items-baseline gap-3 min-w-0">
                  <SectionTag slug={row.slug} name={row.name} />
                  {row.statusHint ? (
                    <span className="type-body text-muted-foreground truncate">
                      · {row.statusHint}
                    </span>
                  ) : null}
                </div>
                <Switch
                  aria-label={`Enable ${row.name} in ${scopeLabel}`}
                  checked={row.enabled}
                  onCheckedChange={(v) => setEnabled(row.slug, Boolean(v))}
                  disabled={pending}
                />
              </li>
            ))}
          </ol>
        )}
      </SettingsSection>

      <SettingsSection title="More sections you can enable">
        {inactive.length === 0 ? (
          <p className="type-body text-muted-foreground">
            Every canonical section is enabled.
          </p>
        ) : (
          <ol>
            {inactive.map((row, idx) => (
              <li
                key={row.slug}
                className={cn(
                  "flex items-center justify-between gap-3 border-b border-border/40 py-2",
                  idx === 0 && "pt-0",
                  idx === inactive.length - 1 && "border-b-0 pb-0",
                )}
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <SectionTag slug={row.slug} name={row.name} />
                  </div>
                  <p className="type-body text-muted-foreground">
                    {row.description}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEnabled(row.slug, true)}
                  disabled={pending}
                  aria-label={`Enable ${row.name} in ${scopeLabel}`}
                >
                  Enable
                </Button>
              </li>
            ))}
          </ol>
        )}
      </SettingsSection>
    </>
  );
}
